/**
 * FiltersBar Component
 *
 * Search and filter controls for the advisory requests dashboard
 */

import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import CloseIcon from '@mui/icons-material/Close';
import Button from 'components/Button';
import { LeadRequestStatus } from 'types/leadRequestsTypes';
import { useTheme } from 'hooks/useTheme';
import * as S from './styles';

export type SortOption = 'date-newest' | 'date-oldest' | 'status' | 'requester';
export type ViewMode = 'grid' | 'list';

interface FiltersBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedStatus: string;
  onStatusChange: (value: string) => void;
  onClearFilters: () => void;
  sortBy: SortOption;
  onSortChange: (value: SortOption) => void;
  viewMode: ViewMode;
  onViewModeChange: (value: ViewMode) => void;
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  searchTerm,
  onSearchChange,
  selectedStatus,
  onStatusChange,
  onClearFilters,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
}) => {
  const hasActiveFilters = searchTerm || selectedStatus;
  const { theme: themeColors } = useTheme();

  const handleRemoveFilter = (filterType: 'search' | 'status') => {
    if (filterType === 'search') {
      onSearchChange('');
    } else if (filterType === 'status') {
      onStatusChange('');
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      [LeadRequestStatus.NEW]: 'Novo',
      [LeadRequestStatus.REQUESTED]: 'Solicitado',
      [LeadRequestStatus.ACCEPTED]: 'Aceito',
      [LeadRequestStatus.REJECTED]: 'Rejeitado',
      [LeadRequestStatus.CANCELED]: 'Cancelado',
    };
    return labels[status] || status;
  };

  return (
    <>
      <S.FiltersContainer>
        <S.SearchBox>
          <TextField
            fullWidth
            placeholder="Pesquise por ID da solicitação ou patrocinador..."
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: themeColors.background.secondary,
              },
            }}
          />
        </S.SearchBox>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtro de status</InputLabel>
          <Select
            value={selectedStatus}
            onChange={e => onStatusChange(e.target.value)}
            label="Filtro de status"
            sx={{
              backgroundColor: themeColors.background.secondary,
            }}
          >
            <MenuItem value="">Todos os statuses</MenuItem>
            <MenuItem value={LeadRequestStatus.NEW}>Novo</MenuItem>
            <MenuItem value={LeadRequestStatus.REQUESTED}>Solicitado</MenuItem>
            <MenuItem value={LeadRequestStatus.ACCEPTED}>Aceito</MenuItem>
            <MenuItem value={LeadRequestStatus.REJECTED}>Rejeitado</MenuItem>
            <MenuItem value={LeadRequestStatus.CANCELED}>Cancelado</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={sortBy}
            onChange={e => onSortChange(e.target.value as SortOption)}
            label="Ordenar por"
            sx={{
              backgroundColor: themeColors.background.secondary,
            }}
          >
            <MenuItem value="date-newest">Data (Mais recente)</MenuItem>
            <MenuItem value="date-oldest">Data (Mais antigo)</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="requester">Nome do solicitante</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newView) => newView && onViewModeChange(newView)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              backgroundColor: themeColors.background.secondary,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#fff',
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 199, 0, 0.2)',
                borderColor: '#FFC700',
                color: '#FFC700',
                '&:hover': {
                  backgroundColor: 'rgba(255, 199, 0, 0.3)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            },
          }}
        >
          <ToggleButton value="grid" aria-label="visualização em grade">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="visualização em lista">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        {hasActiveFilters && (
          <Button variant="minimal" onClick={onClearFilters} size="small">
            Limpar tudo
          </Button>
        )}
      </S.FiltersContainer>

      {hasActiveFilters && (
        <S.FilterChipsContainer>
          {searchTerm && (
            <Chip
              label={`Busca: "${searchTerm}"`}
              onDelete={() => handleRemoveFilter('search')}
              deleteIcon={<CloseIcon />}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 199, 0, 0.2)',
                borderColor: '#FFC700',
                border: '1px solid',
                color: '#fff',
                '& .MuiChip-deleteIcon': {
                  color: '#FFC700',
                  '&:hover': {
                    color: '#fff',
                  },
                },
              }}
            />
          )}
          {selectedStatus && (
            <Chip
              label={`Status: ${getStatusLabel(selectedStatus)}`}
              onDelete={() => handleRemoveFilter('status')}
              deleteIcon={<CloseIcon />}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 199, 0, 0.2)',
                borderColor: '#FFC700',
                border: '1px solid',
                color: '#fff',
                '& .MuiChip-deleteIcon': {
                  color: '#FFC700',
                  '&:hover': {
                    color: '#fff',
                  },
                },
              }}
            />
          )}
        </S.FilterChipsContainer>
      )}
    </>
  );
};

export default FiltersBar;
